import { useCallback, useEffect, useState } from 'react';
import { useHideAppChrome } from '../../layouts/appChromeContext';
import { OPEN_MODAL_SELECTOR, shouldEscapeExitFocusMode } from './focusModeEscape';

const FOCUS_STORAGE_KEY = 'odo:wq:focus';

function readFocusPreference(): boolean {
    try {
        return globalThis.localStorage?.getItem(FOCUS_STORAGE_KEY) === '1';
    } catch {
        return false;
    }
}

function writeFocusPreference(on: boolean): void {
    try {
        globalThis.localStorage?.setItem(FOCUS_STORAGE_KEY, on ? '1' : '0');
    } catch {
        /* quota / private-mode guard */
    }
}

export interface TaskFocusMode {
    focused: boolean;
    toggleFocus: () => void;
}

// Remembered per browser; only takes effect while a task is open, so the plain queue keeps its chrome.
export function useTaskFocusMode(taskOpen: boolean): TaskFocusMode {
    const [preferred, setPreferred] = useState(readFocusPreference);
    const focused = taskOpen && preferred;

    const setPreference = useCallback((next: boolean) => {
        setPreferred(next);
        writeFocusPreference(next);
    }, []);
    const toggleFocus = useCallback(() => setPreference(!preferred), [preferred, setPreference]);

    useHideAppChrome(focused);

    useEffect(() => {
        if (!focused) return undefined;
        let timer: ReturnType<typeof setTimeout> | undefined;
        // Snapshot in capture, before a modal's own Escape handler unmounts it; decide after
        // dispatch so any later listener can still claim the key with preventDefault.
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            const active =
                document.activeElement instanceof HTMLElement ? document.activeElement : null;
            const modalOpen = document.querySelector(OPEN_MODAL_SELECTOR) !== null;
            timer = setTimeout(() => {
                if (shouldEscapeExitFocusMode(event, active, modalOpen)) setPreference(false);
            }, 0);
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => {
            window.removeEventListener('keydown', onKeyDown, true);
            clearTimeout(timer);
        };
    }, [focused, setPreference]);

    return { focused, toggleFocus };
}
